import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  AlertCircle,
  Users,
  Target,
  Trophy,
  Clock,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Flame,
  TrendingUp,
  TrendingDown,
  Timer,
  RefreshCw,
  Copy,
  Check,
  Mail,
  ExternalLink,
  Share2,
  List,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

export default function DashboardPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [expandedBreakdown, setExpandedBreakdown] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [copiedChallenge, setCopiedChallenge] = useState(false);
  const [copiedDashboard, setCopiedDashboard] = useState(false);

  // Get URLs for sharing
  const challengeLink = dashboardData?.challenge?.challenge_id 
    ? `${window.location.origin}/challenge/${dashboardData.challenge.challenge_id}`
    : "";
  const dashboardLink = `${window.location.origin}/dashboard/${token}`;

  const handleCopyChallengeLink = async () => {
    try {
      await navigator.clipboard.writeText(challengeLink);
      setCopiedChallenge(true);
      toast.success("Challenge link copied!");
      setTimeout(() => setCopiedChallenge(false), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const handleCopyDashboardLink = async () => {
    try {
      await navigator.clipboard.writeText(dashboardLink);
      setCopiedDashboard(true);
      toast.success("Dashboard link copied!");
      setTimeout(() => setCopiedDashboard(false), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const handleEmailDashboard = () => {
    const challenge = dashboardData?.challenge;
    const subject = encodeURIComponent(
      `Your StreakChallenge Dashboard: ${challenge?.subject || "Challenge"} - ${challenge?.grade_level || ""}`
    );
    const body = encodeURIComponent(
      `Here's your StreakChallenge dashboard link:\n\n` +
      `📊 Dashboard: ${dashboardLink}\n\n` +
      `Challenge Details:\n` +
      `• Subject: ${challenge?.subject || "N/A"}\n` +
      `• Grade: ${challenge?.grade_level || "N/A"}\n` +
      `• Goal: ${challenge?.streak_target || "N/A"} in a row\n\n` +
      `🔗 Share this challenge with students: ${challengeLink}\n\n` +
      `---\nSent from StreakChallenge`
    );
    
    // Show toast with fallback instructions
    toast.info("Opening your email app… If nothing happens, copy the dashboard link.", {
      duration: 4000,
    });
    
    window.open(`mailto:?subject=${subject}&body=${body}`, "_self");
  };

  const fetchDashboard = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    }
    try {
      const response = await axios.get(`${API}/dashboard/${token}`);
      if (response.data.success) {
        setDashboardData(response.data);
        setLastUpdated(new Date());
        setError(null);
      } else {
        if (!isRefresh) setError("Failed to load dashboard");
      }
    } catch (err) {
      console.error("Error fetching dashboard:", err);
      if (!isRefresh) {
        if (err.response?.status === 404) {
          setError("Dashboard not found. The link may be invalid or expired.");
        } else {
          setError("Failed to load dashboard. Please try again.");
        }
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  // Initial fetch
  useEffect(() => {
    fetchDashboard(false);
  }, [fetchDashboard]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (error) return; // Don't auto-refresh if there's an error
    
    const interval = setInterval(() => {
      fetchDashboard(true);
    }, AUTO_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchDashboard, error]);

  const handleManualRefresh = () => {
    fetchDashboard(true);
  };

  const formatTime = (seconds) => {
    if (!seconds) return "—";
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const formatLastUpdated = (date) => {
    if (!date) return "";
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center" data-testid="dashboard-loading">
        <Card className="max-w-md w-full mx-4 rounded-2xl">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 text-ember mx-auto mb-4 animate-spin" />
            <h2 className="font-heading text-2xl text-sage-900 mb-2">Loading Dashboard...</h2>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center py-8 px-4" data-testid="dashboard-error">
        <Card className="max-w-md w-full rounded-2xl">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-sage-900 mb-2">Oops!</h2>
            <p className="font-body text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => window.location.href = "/"} className="rounded-full">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { challenge, engagement_summary, performance_snapshot, effort_insights, question_breakdown } = dashboardData;

  return (
    <div className="min-h-screen bg-paper py-8 px-4" data-testid="dashboard-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-ember/10 rounded-2xl">
              <BarChart3 className="w-8 h-8 text-ember" />
            </div>
            <h1 className="font-heading text-3xl font-light text-sage-900">
              Challenge Dashboard
            </h1>
          </div>
          <p className="font-body text-muted-foreground">
            {challenge.subject} • {challenge.grade_level} • Target: {challenge.streak_target} in a row
          </p>
          <p className="font-body text-xs text-muted-foreground mt-1">
            {challenge.question_count} questions • Created {new Date(challenge.created_at).toLocaleDateString()}
          </p>
          
          {/* Refresh Button and Last Updated */}
          <div className="flex items-center justify-center gap-3 mt-4">
            <Button
              onClick={() => navigate("/my-challenges")}
              variant="outline"
              size="sm"
              className="rounded-full"
              data-testid="my-challenges-btn"
            >
              <List className="w-4 h-4 mr-2" />
              My Challenges
            </Button>
            <Button
              onClick={handleManualRefresh}
              disabled={refreshing}
              variant="outline"
              size="sm"
              className="rounded-full"
              data-testid="refresh-dashboard-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            {lastUpdated && (
              <span className="font-body text-xs text-muted-foreground" data-testid="last-updated">
                Last updated: {formatLastUpdated(lastUpdated)}
              </span>
            )}
          </div>
          
          {/* Share Actions */}
          <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
            <Button
              onClick={handleCopyChallengeLink}
              variant="outline"
              size="sm"
              className="rounded-full"
              data-testid="copy-challenge-link-btn"
            >
              {copiedChallenge ? (
                <>
                  <Check className="w-4 h-4 mr-1.5 text-green-500" />
                  Copied!
                </>
              ) : (
                <>
                  <Share2 className="w-4 h-4 mr-1.5" />
                  Copy Challenge Link
                </>
              )}
            </Button>
            <Button
              onClick={handleCopyDashboardLink}
              variant="outline"
              size="sm"
              className="rounded-full"
              data-testid="copy-dashboard-link-btn"
            >
              {copiedDashboard ? (
                <>
                  <Check className="w-4 h-4 mr-1.5 text-green-500" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1.5" />
                  Copy Dashboard Link
                </>
              )}
            </Button>
            <Button
              onClick={handleEmailDashboard}
              variant="outline"
              size="sm"
              className="rounded-full"
              data-testid="email-dashboard-btn"
            >
              <Mail className="w-4 h-4 mr-1.5" />
              Email to Myself
            </Button>
          </div>
        </div>

        {/* Engagement Summary */}
        <Card className="border-border bg-card rounded-2xl shadow-md mb-6" data-testid="engagement-summary">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-sage-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-sage-500" />
              Engagement Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-secondary rounded-xl">
                <p className="font-heading text-3xl text-sage-900" data-testid="total-attempts">
                  {engagement_summary.total_attempts}
                </p>
                <p className="font-body text-xs text-muted-foreground">Total Attempts</p>
              </div>
              <div className="text-center p-4 bg-secondary rounded-xl">
                <p className="font-heading text-3xl text-sage-900" data-testid="completion-rate">
                  {engagement_summary.completion_rate_percent}%
                </p>
                <p className="font-body text-xs text-muted-foreground">Completion Rate</p>
              </div>
              <div className="text-center p-4 bg-secondary rounded-xl">
                <p className="font-heading text-3xl text-sage-900 flex items-center justify-center gap-1" data-testid="avg-streak">
                  <Flame className="w-5 h-5 text-ember" />
                  {engagement_summary.avg_longest_streak}
                </p>
                <p className="font-body text-xs text-muted-foreground">Avg Longest Streak</p>
                <p className="font-body text-[10px] text-muted-foreground/70 mt-0.5">Includes bonus streaks</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Performance Snapshot */}
        <Card className="border-border bg-card rounded-2xl shadow-md mb-6" data-testid="performance-snapshot">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-sage-900 flex items-center gap-2">
              <Target className="w-5 h-5 text-sage-500" />
              Performance Snapshot
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Overall Accuracy */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="font-body text-sm text-muted-foreground">Overall Accuracy</span>
                <span className="font-heading text-xl text-sage-900" data-testid="overall-accuracy">
                  {performance_snapshot.overall_accuracy_percent}%
                </span>
              </div>
              <Progress 
                value={performance_snapshot.overall_accuracy_percent} 
                className="h-3"
              />
              <p className="font-body text-xs text-muted-foreground mt-1">
                Based on gradable questions only
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Most Missed Question */}
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl" data-testid="most-missed">
                <div className="flex items-start gap-2 mb-2">
                  <TrendingDown className="w-4 h-4 text-red-500 mt-0.5" />
                  <span className="font-body font-medium text-red-800 text-sm">Most Missed</span>
                </div>
                {performance_snapshot.most_missed_question ? (
                  <>
                    <p className="font-body text-sm text-red-700 mb-2">
                      {performance_snapshot.most_missed_question.question_text}
                    </p>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-red-600 font-medium">
                        {performance_snapshot.most_missed_question.accuracy_percent}% accuracy
                      </span>
                      <span className="text-red-500">
                        {performance_snapshot.most_missed_question.attempts} attempts
                      </span>
                    </div>
                  </>
                ) : (
                  <p className="font-body text-sm text-red-600">No data yet</p>
                )}
              </div>

              {/* Most Accurate Question */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl" data-testid="most-accurate">
                <div className="flex items-start gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-green-500 mt-0.5" />
                  <span className="font-body font-medium text-green-800 text-sm">Most Accurate</span>
                </div>
                {performance_snapshot.most_accurate_question ? (
                  <>
                    <p className="font-body text-sm text-green-700 mb-2">
                      {performance_snapshot.most_accurate_question.question_text}
                    </p>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-green-600 font-medium">
                        {performance_snapshot.most_accurate_question.accuracy_percent}% accuracy
                      </span>
                      <span className="text-green-500">
                        {performance_snapshot.most_accurate_question.attempts} attempts
                      </span>
                    </div>
                  </>
                ) : (
                  <p className="font-body text-sm text-green-600">No data yet</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Effort Insights */}
        <Card className="border-border bg-card rounded-2xl shadow-md mb-6" data-testid="effort-insights">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-sage-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-sage-500" />
              Effort Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-secondary rounded-xl">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  <p className="font-heading text-2xl text-sage-900" data-testid="avg-time-to-goal">
                    {formatTime(effort_insights.avg_time_to_goal_seconds)}
                  </p>
                </div>
                <p className="font-body text-xs text-muted-foreground">Avg Time to Goal</p>
                <p className="font-body text-xs text-muted-foreground">(completed sessions)</p>
              </div>
              <div className="text-center p-4 bg-secondary rounded-xl">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Timer className="w-5 h-5 text-blue-500" />
                  <p className="font-heading text-2xl text-sage-900" data-testid="avg-session-time">
                    {formatTime(effort_insights.avg_session_time_seconds)}
                  </p>
                </div>
                <p className="font-body text-xs text-muted-foreground">Avg Session Time</p>
                <p className="font-body text-xs text-muted-foreground">(all sessions)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Question Breakdown (Expandable) */}
        <Card className="border-border bg-card rounded-2xl shadow-md" data-testid="question-breakdown">
          <CardHeader className="pb-2">
            <button
              onClick={() => setExpandedBreakdown(!expandedBreakdown)}
              className="w-full flex items-center justify-between"
              data-testid="expand-breakdown-btn"
            >
              <CardTitle className="font-heading text-lg text-sage-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-sage-500" />
                Question Breakdown
              </CardTitle>
              {expandedBreakdown ? (
                <ChevronUp className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
          </CardHeader>
          {expandedBreakdown && (
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-2 font-body font-medium text-muted-foreground">#</th>
                      <th className="text-left py-2 px-2 font-body font-medium text-muted-foreground">Question</th>
                      <th className="text-center py-2 px-2 font-body font-medium text-muted-foreground">Accuracy</th>
                      <th className="text-center py-2 px-2 font-body font-medium text-muted-foreground">Attempts</th>
                      <th className="text-center py-2 px-2 font-body font-medium text-muted-foreground">Avg Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {question_breakdown.map((q, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-secondary/50">
                        <td className="py-2 px-2 font-body text-sage-900">{q.question_index + 1}</td>
                        <td className="py-2 px-2 font-body text-sage-700 max-w-[200px] truncate" title={q.question_text}>
                          {q.question_text}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {q.accuracy_percent !== null ? (
                            <span className={`font-body font-medium ${
                              q.accuracy_percent >= 70 ? 'text-green-600' :
                              q.accuracy_percent >= 40 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {q.accuracy_percent}%
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-center font-body text-sage-700">
                          {q.attempts || 0}
                        </td>
                        <td className="py-2 px-2 text-center font-body text-sage-700">
                          {q.avg_time_seconds ? `${q.avg_time_seconds}s` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="font-body text-xs text-muted-foreground">
            Auto-refreshes every 30 seconds. Click Refresh for instant updates.
          </p>
        </div>
      </div>
    </div>
  );
}
